import { useEffect } from "react";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse } from "axios";
import { useOrder } from "@utils/store/cart/useOrder";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { isPayLinkMode } from "@utils/payLink";
import { useAuthReady } from "@provider/LineLoginProvider";

/**
 * 載入目前 context 的草稿訂單到購物車（productList）。
 * 原本掛在 TopBarTools，移除鈴鐺／購物車 UI 後改由此 hook 在 PageWrapper 常駐執行，
 * 否則各頁（瓦斯訂購等）不會載入既有草稿，導致購物車品項不顯示、重複加入時撞 GAS_ITEM_DUPLICATE_LINE。
 *
 * 一次性付款連結（/pay/:orderId?pt=<token>）不依賴 LINE 登入、也沒有 chooseIdentity／
 * context 概念（見 PayLinkView），這裡打的 /platform-order-drafts/current 只會撞出
 * 一支無意義的 401（並可能誤觸發 apiManager 的登入復原）；guard 放在兩個 effect 內部
 * 而不是 hook 開頭，是為了不違反 hooks 規則（hook 仍需每次 render 都呼叫同一組 hooks）。
 */
export function useCurrentDraftSync() {
  const { orderState, setOrderState, syncDraft, clearProducts } = useOrder();
  const { chooseIdentity } = useUser();
  // 等登入塵埃落定才打 API：開機瞬間 sessionStorage 常留著上一個 session 的過期 jwt，
  // 搶跑必吃 401（2026-09-09 dev 實測 6 小時 82 發），既是 Sentry 雜訊、又會誤觸發
  // apiManager 的登入復原（把使用者拖去登出重登）。authReady 之後 jwt 才是這次的。
  const authReady = useAuthReady();

  // 切換身分 → 重新載入該 context 的草稿
  useEffect(() => {
    if (isPayLinkMode()) return;
    setOrderState("loading");
  }, [chooseIdentity?.context_id]);

  useEffect(() => {
    if (isPayLinkMode()) return;
    if (!authReady) return;
    if (orderState !== "loading") return;
    handleRequest({
      asyncRequest: () =>
        api.get("/app-api/platform-order-drafts/current", {
          headers: { "X-Context": buildXContextHeader(chooseIdentity) },
        }),
      successCallback: (res: AxiosResponse) => {
        const { data } = res.data;
        if (data?.draft) {
          syncDraft(data.draft);
        } else {
          clearProducts();
          setOrderState("success");
        }
      },
      errorCallback: (error: unknown) => {
        console.error("取得草稿訂單失敗:", error);
        setOrderState("success");
      },
    });
  }, [orderState, chooseIdentity?.context_id, authReady]);
}
